/**
 * VoiceFamilyUpdateModal — caregiver records a casual update, AI composes
 * 4 polished family-message variants (warm/casual/brief/detailed), caregiver
 * picks one, edits if needed, sends to Family Portal feed.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Loader2, Sparkles, X, Send, AlertTriangle, Heart } from "lucide-react";
import useVoiceRecorder from "@/lib/useVoiceRecorder";

const TONE_META = {
  warm: { l: "Warm", desc: "Friendly, with a personal moment" },
  casual: { l: "Casual", desc: "Like a text message" },
  brief: { l: "Brief", desc: "One-sentence headline" },
  detailed: { l: "Detailed", desc: "Specific moments + activities" },
};

export default function VoiceFamilyUpdateModal({ open, clientId, onClose, onSent }) {
  const [result, setResult] = useState(null);
  const [tone, setTone] = useState("warm");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  const rec = useVoiceRecorder({
    endpoint: "/ai/voice-fill/family-update",
    extra: { client_id: clientId },
    filenamePrefix: "family-update",
    onResult: (data) => {
      setResult(data);
      const suggested = data.suggested_tone || "warm";
      setTone(suggested);
      setDraft(data.messages?.[suggested] || "");
    },
  });

  const onPickTone = (t) => {
    setTone(t);
    setDraft(result?.messages?.[t] || "");
  };

  const close = () => {
    rec.reset(); setResult(null); setDraft(""); setTone("warm");
    onClose?.();
  };

  const send = async () => {
    if (!draft.trim()) { toast.error("Message cannot be empty"); return; }
    setSending(true);
    try {
      await api.post(`/clients/${clientId}/family-updates`, {
        message: draft.trim(),
        tone,
        contains_concern: !!result?.contains_concern,
        transcript: result?.transcript || "",
      });
      toast.success("Family update sent");
      onSent?.();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send update");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="voice-family-update-modal">
      <div className="card-surface w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-clinical-border px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl"
              style={{ background: "#EC48991a", color: "#EC4899" }}>
              <Heart size={18} />
            </div>
            <div>
              <h2 className="font-semibold text-clinical-text">Voice update to family</h2>
              <p className="text-xs text-clinical-subtext">
                {rec.isIdle && !result && "Talk casually about the day — AI writes the polite version."}
                {rec.isRecording && `Recording · ${rec.mmss}`}
                {rec.isProcessing && "Composing 4 tone variants…"}
                {result && "Pick a tone, edit if needed, send."}
              </p>
            </div>
          </div>
          <button type="button" onClick={close}
            className="rounded-lg p-2 text-clinical-subtext hover:bg-clinical-bg"
            aria-label="Close" data-testid="voice-family-update-close">
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-3">
          {!result && (
            <div className="flex flex-col items-center gap-4 py-6">
              {rec.isIdle && (
                <p className="text-sm text-center text-clinical-subtext max-w-md">
                  Example: <i>"Margaret had a great morning. Ate her whole breakfast,
                  walked in the garden with her walker, did her crossword. Slept well."</i>
                </p>
              )}
              {rec.isRecording && (
                <div className="flex flex-col items-center gap-2">
                  <div className="relative h-20 w-20">
                    <div className="absolute inset-0 rounded-full bg-pink-500/20 animate-ping" />
                    <div className="relative h-20 w-20 rounded-full bg-pink-500 flex items-center justify-center">
                      <Mic className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <div className="text-2xl font-mono tabular-nums text-clinical-text" data-testid="voice-family-update-timer">
                    {rec.mmss}
                  </div>
                </div>
              )}
              {rec.isProcessing && (
                <>
                  <Loader2 className="h-10 w-10 animate-spin text-clinical-accent" />
                  <p className="text-sm text-clinical-subtext">Composing in 4 tones…</p>
                </>
              )}
              <button
                type="button"
                onClick={rec.toggle}
                disabled={rec.isProcessing}
                className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition ${
                  rec.isRecording ? "bg-red-600 text-white hover:bg-red-700"
                                  : "bg-clinical-accent text-white hover:opacity-90"
                } disabled:opacity-50`}
                data-testid={rec.isRecording ? "voice-family-update-stop" : "voice-family-update-start"}
              >
                {rec.isRecording ? <Square className="h-4 w-4 fill-current" /> : <Mic className="h-4 w-4" />}
                {rec.isRecording ? "Stop & compose" : rec.isProcessing ? "Composing…" : "Start recording"}
              </button>
            </div>
          )}

          {result && (
            <div className="space-y-3" data-testid="voice-family-update-results">
              {result.transcript && (
                <details className="card-surface p-3 bg-clinical-bg/40">
                  <summary className="cursor-pointer text-xs font-semibold uppercase text-clinical-subtext">
                    Transcript
                  </summary>
                  <p className="mt-2 text-sm text-clinical-text whitespace-pre-line">{result.transcript}</p>
                </details>
              )}

              {result.contains_concern && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  <div className="flex items-center gap-1.5 font-semibold mb-1">
                    <AlertTriangle size={14} /> Concern flagged
                  </div>
                  This update describes a concerning event. Make sure the
                  corresponding incident report has been filed before sending.
                </div>
              )}

              {(result.warnings || []).length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
                  <ul className="list-disc pl-5 space-y-0.5">
                    {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              {/* Tone picker */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                {Object.entries(TONE_META).map(([k, m]) => {
                  const active = tone === k;
                  const suggested = result.suggested_tone === k;
                  return (
                    <button key={k} type="button" onClick={() => onPickTone(k)}
                      className={`rounded-lg border px-2 py-2 text-left text-xs transition ${
                        active
                          ? "border-clinical-accent bg-clinical-accent/10"
                          : "border-clinical-border bg-white hover:border-clinical-accent/50"
                      }`}
                      data-testid={`voice-family-update-tone-${k}`}
                    >
                      <div className={`font-semibold ${active ? "text-clinical-accent" : "text-clinical-text"}`}>
                        {m.l}{suggested && " ★"}
                      </div>
                      <div className="text-clinical-subtext mt-0.5">{m.desc}</div>
                    </button>
                  );
                })}
              </div>

              {/* Editable message */}
              <div>
                <label htmlFor="vfu-message" className="label-eyebrow">Message preview · edit before sending</label>
                <textarea
                  id="vfu-message"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={5}
                  className="input-clinical resize-y mt-1"
                  data-testid="voice-family-update-draft"
                />
                <div className="mt-1 text-xs text-clinical-subtext">
                  {draft.length}/2000 chars · sent as {tone}
                </div>
              </div>
            </div>
          )}
        </div>

        {result && (
          <div className="border-t border-clinical-border px-5 py-3 flex items-center justify-between">
            <button type="button" onClick={() => { setResult(null); rec.reset(); setDraft(""); }}
              className="text-sm text-clinical-subtext hover:text-clinical-text"
              data-testid="voice-family-update-redo">
              Record again
            </button>
            <button type="button" onClick={send} disabled={sending || !draft.trim()}
              className="btn-primary" data-testid="voice-family-update-send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send to family
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
